import { useTranslation } from 'react-i18next'
import { IconButton, Tooltip } from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import { MuiOtpInput } from 'mui-one-time-password-input'

import Dialog from '../common/dialog'
import { Pocket } from '../../utils/dexie/db'
import { countriesCurrencies } from '../../lib'
import { useSocket } from '../../hooks/use-socket'
import { FormError, SystemUser } from '../../types'
import SendIcon from '../../assets/icons/send-icon.svg'
import TrashIcon from '../../assets/icons/trash-icon.svg'
import AccountIcon from '../../assets/icons/account-icon.svg'
import { AuthContext, NotificationsContext } from '../../contexts'
import WalletIconGreen from '../../assets/icons/wallet-icon-green.svg'
import { checkTwoFactorAuth, requestAccessToken } from '../../api/auth.api'
import { handleCheckIfOtpCodeIsValid, numberWithCommas } from '../../utils'
import { CreatePocketModal } from '../pockets-management/create-pocket-modal'
import { createSystemUser, deleteSystemUserData, resendActivationEmail } from '../../api/system-users.api'
import { createCustomerCompanyPocket, deleteCustomerCompanyPocket } from '../../api/customers.api'
import { getCustomerCompanyPockets, getCustomerCompanyComplianceUsers } from '../../api/app-queries.api'
import { CreateCustomerCompanyComplianceUserModal } from './create-customer-company-compliance-user-modal'

export const CustomerCompanyManageDataModal = ({
  customerCompanyId,
  handleCloseModal,
  handleCloseSession,
  customerCompanyDataToUpdate
}: {
  customerCompanyId?: string
  handleCloseModal: (openStateToSet: boolean) => void
  handleCloseSession: () => void
  customerCompanyDataToUpdate: string
}) => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'

  const { isAuth } = useContext(AuthContext)
  const { appDataSocket, isAppDataSocketInitialized } = useSocket()
  const { updateNewNotificationStatus } = useContext(NotificationsContext)
  const { t, i18n } = useTranslation(['customer-company-details-management', 'payments-to-review-management', 'pockets-management', 'system-users-management'])

  const [otpCode, setOtpCode] = useState('')
  const [errors, setErrors] = useState<FormError>({})
  const [generalError, setGeneralError] = useState<boolean>(false)
  const [pocketToDeleteId, setPocketToDeleteId] = useState<string>('')
  const [dialogIsLoading, setDialogIsLoading] = useState<boolean>(true)
  const [deletePocketAction, setDeletePocketAction] = useState<boolean>(false)
  const [pocketNameDataToCreate, setPocketNameDataToCreate] = useState<string>('')
  const [openConfirmationModal, setOpenConfirmationModal] = useState<boolean>(false)
  const [openCreatePocketModal, setOpenCreatePocketModal] = useState<boolean>(false)
  const [complianceUserToDeleteId, setComplianceUserToDeleteId] = useState<string>('')
  const [deleteComplianceUserAction, setDeleteComplianceUserAction] = useState<boolean>(false)
  const [dialogConfimationIsLoading, setDialogConfimationIsLoading] = useState<boolean>(false)
  const [createPocketModalIsLoading, setCreatePocketModalIsLoading] = useState<boolean>(false)
  const [customerCompanyPocketsData, setCustomerCompanyPocketsData] = useState<Array<Pocket>>([])
  const [customerCompanyComplianceUsersData, setCustomerCompanyComplianceUsersData] = useState<Array<SystemUser>>([])
  const [openCreateCustomerCompanyComplianceUserModal, setOpenCreateCustomerCompanyComplianceUserModal] = useState<boolean>(false)
  const [complianceUserDataToCreate, setComplianceUserDataToCreate] = useState<{ userEmailData: string; userFirstNameData: string; userLastNameData: string } | null>(null)

  useEffect(() => {
    if (!appDataSocket) return

    if (customerCompanyDataToUpdate === 'compliance-users') {
      appDataSocket.on('addNewSystemUser', async () => {
        handleGetCustomerCompanyComplianceUsersFromRemote()
      })

      appDataSocket.on('updateSystemUser', async () => {
        handleGetCustomerCompanyComplianceUsersFromRemote()
      })

      appDataSocket.on('deleteSystemUser', async () => {
        handleGetCustomerCompanyComplianceUsersFromRemote()
      })
    }

    if (customerCompanyDataToUpdate === 'pockets') {
      appDataSocket.on('addCustomerCompanyPocket', async (dataFromSocket: any) => {
        const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
        const selectedCurrency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

        if (selectedCurrency === dataFromSocket.currency) {
          handleGetCustomerCompanyPocketsFromRemote()
        }
      })

      appDataSocket.on('updateCustomerCompanyPocket', async (dataFromSocket: any) => {
        const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
        const selectedCurrency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

        if (selectedCurrency === dataFromSocket.currency) {
          handleGetCustomerCompanyPocketsFromRemote()
        }
      })

      appDataSocket.on('deleteCustomerCompanyPocket', async (dataFromSocket: any) => {
        const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
        const selectedCurrency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

        if (selectedCurrency === dataFromSocket.currency) {
          handleGetCustomerCompanyPocketsFromRemote()
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appDataSocket, isAppDataSocketInitialized])

  useEffect(() => {
    if (customerCompanyDataToUpdate === 'pockets') {
      handleGetCustomerCompanyPocketsData()
    }

    if (customerCompanyDataToUpdate === 'compliance-users') {
      handleGetCustomerCompanyComplianceUsersData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleGetCustomerCompanyPocketsData = () => {
    let localCustomerCompanyPocketsToSet: any = {}
    const localCustomerCompanyPockets = globalThis.sessionStorage.getItem(`customerCompanyPockets:${customerCompanyId}:${selectedCountry}`)

    if (localCustomerCompanyPockets) {
      localCustomerCompanyPocketsToSet = JSON.parse(localCustomerCompanyPockets)
      handleSetCustomerCompanyPocketsInitialData(localCustomerCompanyPocketsToSet)
    }

    handleGetCustomerCompanyPocketsFromRemote()
  }

  const handleGetCustomerCompanyPocketsFromRemote = async () => {
    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))
      const { data } = await getCustomerCompanyPockets(String(customerCompanyId), selectedCountry, accessToken)

      globalThis.sessionStorage.setItem(`customerCompanyPockets:${customerCompanyId}:${selectedCountry}`, JSON.stringify(data))
      handleSetCustomerCompanyPocketsInitialData(data)
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      }
    }
  }

  const handleSetCustomerCompanyPocketsInitialData = (dataToSet: any) => {
    setCustomerCompanyPocketsData(dataToSet.customerCompanyPockets)
    setDialogIsLoading(false)
  }

  const handleGetCustomerCompanyComplianceUsersData = () => {
    let localCustomerCompanyComplianceUsersToSet: any = {}
    const localCustomerCompanyComplianceUsers = globalThis.sessionStorage.getItem(`customerCompanyComplianceUsers:${customerCompanyId}`)

    if (localCustomerCompanyComplianceUsers) {
      localCustomerCompanyComplianceUsersToSet = JSON.parse(localCustomerCompanyComplianceUsers)
      handleSetCustomerCompanyComplianceUsersInitialData(localCustomerCompanyComplianceUsersToSet)
    }

    handleGetCustomerCompanyComplianceUsersFromRemote()
  }

  const handleGetCustomerCompanyComplianceUsersFromRemote = async () => {
    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))
      const { data } = await getCustomerCompanyComplianceUsers(String(customerCompanyId), accessToken)

      globalThis.sessionStorage.setItem(`customerCompanyComplianceUsers:${customerCompanyId}`, JSON.stringify(data))
      handleSetCustomerCompanyComplianceUsersInitialData(data)
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      }
    }
  }

  const handleSetCustomerCompanyComplianceUsersInitialData = (dataToSet: any) => {
    setCustomerCompanyComplianceUsersData(dataToSet.customerCompanyComplianceUsers)
    setDialogIsLoading(false)
  }

  const handleCheckTwoFactorAuth = async () => {
    const { otpCodeIsValid, formErrors } = handleCheckIfOtpCodeIsValid(otpCode, t)

    if (!otpCodeIsValid) {
      return setErrors({ ...formErrors })
    }

    try {
      setErrors({})
      setCreatePocketModalIsLoading(true)
      setDialogConfimationIsLoading(true)
      const {
        data: { accessToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))
      await checkTwoFactorAuth(otpCode, accessToken)

      return { success: true, accessToken }
    } catch (error: any) {
      setOtpCode('')
      console.log(error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.codeInvalid', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.codeExpired', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setCreatePocketModalIsLoading(false)
      setDialogConfimationIsLoading(false)
    }

    return { success: false, accessToken: '' }
  }

  const handleDeletePocket = async () => {
    const { success, accessToken } = (await handleCheckTwoFactorAuth()) as { success: boolean; accessToken: string }

    if (success) {
      try {
        setOtpCode('')
        await deleteCustomerCompanyPocket({ pocketToDeleteId, customerCompanyId }, accessToken)

        handleGetCustomerCompanyPocketsData()
        updateNewNotificationStatus?.(true, 'pocket-deleted')
        setOpenConfirmationModal(false)
        setOpenCreatePocketModal(false)
        setCreatePocketModalIsLoading(false)
        setDialogConfimationIsLoading(false)
      } catch (error: any) {
        setOtpCode('')
        console.log(error, error?.response)
        if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
          handleCloseSession()
        } else if (error?.response?.status === 401) {
          setErrors({ generalError: t('formGeneralErrors.codeInvalid', { ns: 'common' }) })
        } else if (error?.response?.status === 404) {
          setErrors({ generalError: t('formGeneralErrors.codeExpired', { ns: 'common' }) })
        } else if (error?.response?.status === 403) {
          setErrors({ generalError: t('formGeneralErrors.stillOperating', { ns: 'common' }) })
        } else if (error?.response?.status === 406) {
          setErrors({ generalError: t('formGeneralErrors.pocketTransferForbidden', { ns: 'common' }) })
        } else {
          setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
        }

        setCreatePocketModalIsLoading(false)
        setDialogConfimationIsLoading(false)
        return
      }
    }
  }

  const handleCreatePocket = async () => {
    const { otpCodeIsValid, formErrors } = handleCheckIfOtpCodeIsValid(otpCode, t)

    if (!otpCodeIsValid) {
      return setErrors({ ...formErrors })
    }

    try {
      setErrors({})
      setCreatePocketModalIsLoading(true)
      setDialogConfimationIsLoading(true)
      const {
        data: { accessToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))
      await checkTwoFactorAuth(otpCode, accessToken)
    } catch (error: any) {
      setOtpCode('')
      console.log(error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.codeInvalid', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.codeExpired', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setCreatePocketModalIsLoading(false)
      setDialogConfimationIsLoading(false)
      return
    }

    try {
      setOtpCode('')
      const currency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()
      const {
        data: { accessToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))
      await createCustomerCompanyPocket({ pocketName: pocketNameDataToCreate, currency, customerCompanyId }, accessToken)

      await handleGetCustomerCompanyPocketsData()
      updateNewNotificationStatus?.(true, 'pocket-created')
      setOpenConfirmationModal(false)
      setOpenCreatePocketModal(false)
      setCreatePocketModalIsLoading(false)
      setDialogConfimationIsLoading(false)
    } catch (error: any) {
      setOtpCode('')
      console.log(error, error?.response)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.codeInvalid', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.codeExpired', { ns: 'common' }) })
      } else if (error?.response?.status === 406) {
        setErrors({ generalError: t('formGeneralErrors.pocketTransferForbidden', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setCreatePocketModalIsLoading(false)
      setDialogConfimationIsLoading(false)
      return
    }
  }

  const handleDeleteCustomerCompanyComplianceUser = async () => {
    const { success, accessToken } = (await handleCheckTwoFactorAuth()) as { success: boolean; accessToken: string }

    if (success) {
      try {
        setOtpCode('')
        await deleteSystemUserData(complianceUserToDeleteId, accessToken)

        await handleGetCustomerCompanyComplianceUsersFromRemote()
        updateNewNotificationStatus?.(true, 'system-users-deleted')
        setOpenConfirmationModal(false)
        setCreatePocketModalIsLoading(false)
        setDialogConfimationIsLoading(false)
        setOpenCreateCustomerCompanyComplianceUserModal(false)
      } catch (error: any) {
        setOtpCode('')
        console.log(error, error?.response)
        if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
          handleCloseSession()
        } else if (error?.response?.status === 401 && error?.response?.data?.message === 'Not authorized: Exists') {
          setGeneralError(true)
          setOpenConfirmationModal(false)
        } else if (error?.response?.status === 401) {
          setErrors({ generalError: t('formGeneralErrors.codeInvalid', { ns: 'common' }) })
        } else if (error?.response?.status === 404) {
          setErrors({ generalError: t('formGeneralErrors.codeExpired', { ns: 'common' }) })
        } else if (error?.response?.status === 406) {
          setErrors({ generalError: t('formGeneralErrors.pocketTransferForbidden', { ns: 'common' }) })
        } else {
          setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
        }

        setCreatePocketModalIsLoading(false)
        setDialogConfimationIsLoading(false)
        return
      }
    }
  }

  const handleCreateCustomerCompanyComplianceUser = async () => {
    const { otpCodeIsValid, formErrors } = handleCheckIfOtpCodeIsValid(otpCode, t)

    if (!otpCodeIsValid) {
      return setErrors({ ...formErrors })
    }

    try {
      setErrors({})
      setCreatePocketModalIsLoading(true)
      setDialogConfimationIsLoading(true)
      const {
        data: { accessToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))
      await checkTwoFactorAuth(otpCode, accessToken)
    } catch (error: any) {
      setOtpCode('')
      console.log(error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.codeInvalid', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.codeExpired', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setDialogConfimationIsLoading(false)
      return
    }

    try {
      setOtpCode('')
      const {
        data: { accessToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))
      const systemUserData = {
        activated: false,
        role: 'super-admin-client',
        belongsTo: customerCompanyId,
        subRole: 'compliance-specialist',
        username: complianceUserDataToCreate?.userEmailData,
        lastName: complianceUserDataToCreate?.userLastNameData,
        firstName: complianceUserDataToCreate?.userFirstNameData
      }
      await createSystemUser(systemUserData as unknown as SystemUser, accessToken)

      await handleGetCustomerCompanyComplianceUsersFromRemote()
      updateNewNotificationStatus?.(true, 'system-users-created')
      setOpenConfirmationModal(false)
      setCreatePocketModalIsLoading(false)
      setDialogConfimationIsLoading(false)
      setOpenCreateCustomerCompanyComplianceUserModal(false)
    } catch (error: any) {
      setOtpCode('')
      console.log(error, error?.response)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401 && error?.response?.data?.message === 'Not authorized: Exists') {
        setGeneralError(true)
        setOpenConfirmationModal(false)
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.codeInvalid', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.codeExpired', { ns: 'common' }) })
      } else if (error?.response?.status === 406) {
        setErrors({ generalError: t('formGeneralErrors.pocketTransferForbidden', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setCreatePocketModalIsLoading(false)
      setDialogConfimationIsLoading(false)
      return
    }
  }

  const handleResendSystemUserInvitation = async (selectedSystemUserData: SystemUser) => {
    setDialogIsLoading(true)

    try {
      const systemUserDataToSend = {
        systemUserId: String(selectedSystemUserData._id),
        systemUserEmail: selectedSystemUserData.username,
        systemUserLanguage: i18n && i18n.language.indexOf('es') !== -1 ? 'es' : 'en'
      }

      const accessTokenData = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await resendActivationEmail(systemUserDataToSend, accessTokenData.data.accessToken)

      setDialogIsLoading(false)
      updateNewNotificationStatus?.(true, 'resend-email-notification-success')
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else {
        setDialogIsLoading(false)
        updateNewNotificationStatus?.(true, 'resend-email-notification-error')
      }
    } finally {
      setDialogIsLoading(false)
    }
  }

  return (
    <>
      <Dialog
        minWidth="600px"
        maxHeight="800px"
        hideActionsButtons={false}
        dialogIsLoading={dialogIsLoading}
        handleDialogAction={() => {
          if (customerCompanyDataToUpdate === 'pockets') {
            setOpenCreatePocketModal(true)
          }

          if (customerCompanyDataToUpdate === 'compliance-users') {
            setOpenCreateCustomerCompanyComplianceUserModal(true)
          }
        }}
        actionButtonText={customerCompanyDataToUpdate === 'pockets' ? t('customerCompanyManageModal.createWallet') : t('customerCompanyManageModal.createComplianceUser')}
        dialogTitle={customerCompanyDataToUpdate === 'pockets' ? t('customerCompanyManageModal.walletsManagement') : t('customerCompanyManageModal.complianceUsersManagement')}
        handleDialogOpenState={(openStateToSet: boolean) => handleCloseModal(openStateToSet)}
      >
        <div className="documents-list-container">
          {customerCompanyDataToUpdate === 'compliance-users' && (
            <>
              {customerCompanyComplianceUsersData.length === 0 && (
                <div className="customer-details-list-item-data-container">
                  <div style={{ color: '#737373' }}>{t('customerCompanyManageModal.thereAreNoUsersYet')}</div>
                </div>
              )}
              {customerCompanyComplianceUsersData.map((customerCompanyComplianceUserData: SystemUser) => {
                return (
                  <div
                    key={customerCompanyComplianceUserData._id}
                    className="customer-details-list-item-data-container"
                  >
                    <div
                      style={{
                        gap: '12px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <img
                        style={{
                          width: '24px'
                        }}
                        src={AccountIcon}
                        alt="account-icon"
                      />
                      <div style={{ color: '#737373' }}>{`${customerCompanyComplianceUserData.firstName.toUpperCase()} ${customerCompanyComplianceUserData.lastName?.toUpperCase()}`}</div>
                    </div>
                    <div
                      style={{
                        gap: '12px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {customerCompanyComplianceUserData.activated === false && (
                        <Tooltip
                          title={t('tooltips.resendActivationEmail', { ns: 'system-users-management' })}
                          placement="top"
                        >
                          <IconButton
                            edge="end"
                            sx={{ color: '#107569' }}
                            onClick={(event) => {
                              event.stopPropagation()
                              handleResendSystemUserInvitation(customerCompanyComplianceUserData)
                            }}
                            aria-label="toggle password visibility"
                          >
                            <img
                              src={SendIcon}
                              alt="send-icon"
                            />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip
                        title={t('tooltips.sureToDeletePocket', { ns: 'system-users-management' })}
                        placement="top"
                      >
                        <IconButton
                          edge="end"
                          sx={{ color: '#107569' }}
                          onClick={(event) => {
                            event.stopPropagation()
                            setComplianceUserToDeleteId(String(customerCompanyComplianceUserData._id))
                            setOpenConfirmationModal(true)
                            setDeleteComplianceUserAction(true)
                          }}
                          aria-label="toggle password visibility"
                        >
                          <img
                            src={TrashIcon}
                            alt="trash-icon"
                          />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                )
              })}
            </>
          )}
          {customerCompanyDataToUpdate === 'pockets' && (
            <>
              {customerCompanyPocketsData.map((customerCompanyPocketsData: Pocket) => {
                return (
                  <div
                    key={customerCompanyPocketsData._id}
                    className="customer-details-list-item-data-container"
                  >
                    <div
                      style={{
                        gap: '12px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <img
                        style={{
                          width: '24px'
                        }}
                        src={WalletIconGreen}
                        alt="wallet-icon-green"
                      />
                      <div style={{ color: '#737373' }}>{customerCompanyPocketsData.name?.toUpperCase()}</div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'row', gap: '12px' }}>
                      <div
                        style={{
                          gap: '12px',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <div style={{ color: '#737373' }}>{`$ ${numberWithCommas(Number(customerCompanyPocketsData.balance))} ${customerCompanyPocketsData.currency?.toUpperCase()}`}</div>
                      </div>
                      {customerCompanyPocketsData.main === false && (
                        <div
                          style={{
                            gap: '12px',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <Tooltip
                            title={t('tooltips.sureToDeletePocket', { ns: 'system-users-management' })}
                            placement="top"
                          >
                            <IconButton
                              edge="end"
                              sx={{ color: '#107569' }}
                              onClick={(event) => {
                                event.stopPropagation()
                                setPocketToDeleteId(String(customerCompanyPocketsData._id))
                                setDeletePocketAction(true)
                                setOpenConfirmationModal(true)
                              }}
                              aria-label="toggle password visibility"
                            >
                              <img
                                src={TrashIcon}
                                alt="trash-icon"
                              />
                            </IconButton>
                          </Tooltip>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </>
          )}
        </div>
      </Dialog>
      {openCreateCustomerCompanyComplianceUserModal && (
        <CreateCustomerCompanyComplianceUserModal
          generalError={generalError}
          isLoading={createPocketModalIsLoading}
          handleCreateCustomerCompanyComplianceUser={({ userEmailData, userFirstNameData, userLastNameData }: { userEmailData: string; userFirstNameData: string; userLastNameData: string }) => {
            setComplianceUserDataToCreate({ userEmailData, userFirstNameData, userLastNameData })
            setOpenConfirmationModal(true)
          }}
          handleOpenStateOfModal={(openStateToSet: boolean) => setOpenCreateCustomerCompanyComplianceUserModal(openStateToSet)}
        />
      )}
      {openCreatePocketModal && (
        <CreatePocketModal
          isLoading={createPocketModalIsLoading}
          handleCreatePocket={(pocketNameData: string) => {
            setPocketNameDataToCreate(pocketNameData)
            setOpenConfirmationModal(true)
          }}
          handleOpenStateOfModal={(openStateToSet: boolean) => setOpenCreatePocketModal(openStateToSet)}
        />
      )}
      {openConfirmationModal && (
        <Dialog
          errors={errors}
          actionButtonWarningStyle
          handleDialogAction={() => {
            if (deleteComplianceUserAction) {
              handleDeleteCustomerCompanyComplianceUser()
              return
            }

            if (deletePocketAction) {
              handleDeletePocket()
              return
            }

            if (customerCompanyDataToUpdate === 'pockets') {
              handleCreatePocket()
              return
            }

            handleCreateCustomerCompanyComplianceUser()
          }}
          handleDialogOpenState={(openState: boolean) => {
            if (openState === false) {
              setErrors({})
              setOtpCode('')
              setDeletePocketAction(false)
              setDeleteComplianceUserAction(false)
            }

            setOpenConfirmationModal(openState)
          }}
          dialogIsLoading={dialogConfimationIsLoading}
          dialogTitle={t('customerCompanyManageModal.confirmPocketCreationModal.title')}
          actionButtonText={t('customerCompanyManageModal.confirmPocketCreationModal.actionButtonText')}
        >
          <div
            className="step"
            style={{ marginTop: '0px', gap: '16px' }}
          >
            <div
              className="step-instruction-container"
              style={{ fontSize: '14px', color: '#F59E0B' }}
            >
              {t('checkCodeInstruction', { ns: 'two-factor-auth' })}
            </div>
            <MuiOtpInput
              length={6}
              value={otpCode}
              onChange={(newValue: string) => setOtpCode(newValue)}
              className={errors?.otpCodeError ? 'MuiOtpInput-Box-inner MuiOtpInput-Box-error' : 'MuiOtpInput-Box-inner'}
            />
          </div>
        </Dialog>
      )}
    </>
  )
}
